import React, { useEffect, useState, useRef, useCallback } from 'react';
import Layout from '../components/ui/Layout';
import Button from '../components/ui/Button';
import Card, { CardBody, CardHeader } from '../components/ui/Card';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import Pagination from '@mui/material/Pagination';
import { QualityAssessmentModal } from '../components/QualityAssessmentModal';
import { fetchWithAuth } from '../api';
import { getCurrentUser, removeToken } from '../auth';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface ClientCheckItem {
  id: number;
  audio_url?: string | null;
  audio_path?: string | null;
  transcript?: string | null;
  model_name?: string | null;
  sale_probability: number;
  sale_probability_explanation: string;
  conversation_outcome: string;
  conversation_summary: string;
  client_mood_score: number;
  client_mood_explanation: string;
  client_facts: string[];
  client_needs: string;
  client_objections: string;
  manager_politeness_score: number;
  manager_politeness_explanation: string;
  manager_presentation: string;
  objection_handling_score: number;
  objection_handling_explanation: string;
  created_at?: string;
}

const ClientCallCheckPage: React.FC = () => {
  const user = getCurrentUser();
  const navigate = useNavigate();
  const [audioUrl, setAudioUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<ClientCheckItem[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<ClientCheckItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleLogout = () => {
    removeToken();
    navigate('/login');
  };

  const loadChecks = useCallback(async (targetPage: number = page) => {
    try {
      setLoading(true);
      const offset = (targetPage - 1) * pageSize;
      const resp = await fetchWithAuth(`${API_URL}/quality-control/assessments/client?limit=${pageSize}&offset=${offset}`);
      const data = await resp.json();
      setItems(data?.data || []);
      setTotal(data?.pagination?.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить проверки');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize]);

  useEffect(() => {
    void loadChecks(page);
  }, [page, loadChecks]);

  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);
      if (!audioUrl) {
        setError('Укажите ссылку на аудиозапись');
        setLoading(false);
        return;
      }
      const resp = await fetchWithAuth(`${API_URL}/quality-control/assessments/client/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ audio_url: audioUrl }),
      });
      const created = await resp.json();
      setAudioUrl('');
      // reload first page
      setPage(1);
      await loadChecks(1);
      setSelected(created);
      setModalOpen(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Ошибка при создании проверки');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <Layout user={user} onLogout={handleLogout}>
      <div className="dashboard" style={{ width: '100%', padding: isMobile ? '8px' : '24px' }}>
        <Card>
          <CardHeader>
            <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Проверка звонков</h3>
          </CardHeader>
          <CardBody>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', flexDirection: isMobile ? 'column' : 'row' }}>
              <TextField
                label="Ссылка на аудио (mp3)"
                value={audioUrl}
                onChange={(e) => setAudioUrl(e.target.value)}
                size="small"
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? 'auto' : 400 }}
              />
              <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
                <Button variant="primary" onClick={handleSubmit} disabled={loading} fullWidth={isMobile}>
                  {loading ? '...' : 'Проверить ссылку'}
                </Button>

                {/* Скрытый инпут и стилизованная кнопка загрузки */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const file = e.target.files && e.target.files[0];
                    if (!file) return;
                    try {
                      setLoading(true);
                      setError(null);
                      const form = new FormData();
                      form.append('file', file);
                      const token = localStorage.getItem('token');
                      const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
                      const resp = await fetch(`${API_URL}/quality-control/assessments/client/upload`, {
                        method: 'POST',
                        headers,
                        body: form,
                      });
                      if (!resp.ok) {
                        const err = await resp.json().catch(() => ({}));
                        throw new Error(err.message || 'Upload failed');
                      }
                      const created = await resp.json();
                      setPage(1);
                      await loadChecks(1);
                      setSelected(created);
                      setModalOpen(true);
                    } catch (err: any) {
                      setError(err?.message || 'Ошибка загрузки');
                    } finally {
                      setLoading(false);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }
                  }}
                />
                <Button variant="ghost" onClick={() => fileInputRef.current?.click()} disabled={loading} fullWidth={isMobile}>
                  {loading ? '...' : 'Загрузить файл'}
                </Button>
              </Stack>

              {error && (
                <Typography color="error" sx={{ ml: isMobile ? 0 : 2, mt: isMobile ? 1 : 0, fontSize: 14 }}>{error}</Typography>
              )}
            </Box>
          </CardBody>
        </Card>

        <div style={{ height: 16 }} />

        <Card>
          <CardHeader>
            <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.25rem' }}>Результаты ({total})</h3>
          </CardHeader>
          <CardBody>
            <Box sx={{ p: isMobile ? 1 : 2 }}>
              {items.length === 0 ? (
                <Typography>Проверок пока нет</Typography>
              ) : (
                <Box>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '400px' : 'auto' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f5f5f5' }}>
                          <th style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>Дата</th>
                          <th style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>Вер-ть</th>
                          <th style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>Итог</th>
                          <th style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(item => (
                          <tr key={item.id}>
                            <td style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>{item.created_at ? new Date(item.created_at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                            <td style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>{item.sale_probability}%</td>
                            <td style={{ padding: 8, border: '1px solid #ddd', fontSize: 13 }}>{item.conversation_outcome || '-'}</td>
                            <td style={{ padding: 8, border: '1px solid #ddd' }}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => { setSelected(item); setModalOpen(true); }}
                                className={isMobile ? "p-1 text-xs" : ""}
                              >
                                Открыть
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} size={isMobile ? "small" : "medium"} />
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </CardBody>
        </Card>

        <QualityAssessmentModal open={modalOpen} onClose={() => setModalOpen(false)} assessment={selected as any} />
      </div>
    </Layout>
  );
};

export default ClientCallCheckPage; 